import { config } from '../utils/config';
import cacheService from './cacheService';
import { blogAuthHeaders, mergeBlogAuthHeaders, mintAdminJwtFromEnv } from '../utils/adminApiAuth';

// Presigned media URLs expire server-side in ~15m; keep client cache shorter than that.
const IMAGES_PRESIGN_CACHE_MS = 8 * 60 * 1000;
const isLocalDev =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Service to fetch images from database WITH CACHING
class ImageService {
  constructor() {
    this.baseURL = config.API_URL;
    this.IMAGES_CACHE_KEY = 'vardaan_all_images_presign_v1';
  }

  // Fetch all images from database WITH CACHING (JWT admin Bearer required by API; paginates client-side)
  async getAllImages() {
    console.log('🌐 ImageService: Fetching all images...');

    try {
      const cachedImages = await cacheService.getCache(this.IMAGES_CACHE_KEY);
      if (cachedImages) {
        console.log('✅ ImageService: Loaded images from cache!');
        console.log('📦 Cached images count:', cachedImages.data?.length || 0);
        return cachedImages;
      }

      let headers = {
        ...blogAuthHeaders()
      };
      let hasBearerToken = typeof headers.Authorization === 'string' && headers.Authorization.startsWith('Bearer ');

      // /api/images is an admin-protected endpoint. On public localhost pages without admin JWT,
      // try minting a local JWT first (when REACT_APP_ADMIN_API_KEY is configured).
      if (!hasBearerToken && isLocalDev) {
        const blogsApi = config.getFullApiUrl ? config.getFullApiUrl('/api/blogs') : `${this.baseURL}/api/blogs`;
        const minted = await mintAdminJwtFromEnv(blogsApi);
        if (minted) {
          headers = { ...blogAuthHeaders() };
          hasBearerToken = typeof headers.Authorization === 'string' && headers.Authorization.startsWith('Bearer ');
        } else {
          console.log('ℹ️ ImageService: Skipping admin media API in local public mode (no admin JWT).');
          return { success: true, data: [] };
        }
      }

      // In production/public mode this endpoint is admin-only (JWT). Skip the call entirely
      // to prevent expected 401s from polluting server logs.
      if (!hasBearerToken) {
        console.log('ℹ️ ImageService: No admin JWT available; skipping /api/images and using local fallback assets.');
        const fallback = { success: true, data: [] };
        await cacheService.setCache(this.IMAGES_CACHE_KEY, fallback, IMAGES_PRESIGN_CACHE_MS);
        return fallback;
      }

      const merged = [];
      let page = 1;
      const limit = 20;
      let hasMore = true;

      while (hasMore) {
        const url = `${this.baseURL}/api/images?page=${page}&limit=${limit}`;
        const response = await fetch(url, { headers });
        console.log('📡 API Response status:', response.status, url);

        if (response.status === 401 || response.status === 403) {
          // Public pages do not carry admin JWT; treat auth failure as expected
          // and gracefully fall back to local assets without noisy error logs.
          if (!hasBearerToken) {
            console.log(`ℹ️ ImageService: Media API unauthorized in public mode (${response.status}); using local fallback assets.`);
            const fallback = { success: true, data: [] };
            await cacheService.setCache(this.IMAGES_CACHE_KEY, fallback, IMAGES_PRESIGN_CACHE_MS);
            return fallback;
          }
          throw new Error(`Failed to fetch images: ${response.status} ${response.statusText}`);
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch images: ${response.status} ${response.statusText}`);
        }

        const body = await response.json();
        if (!body.success || !Array.isArray(body.data)) {
          throw new Error('Invalid images API response');
        }

        merged.push(...body.data);
        hasMore = Boolean(body.pagination && body.pagination.hasMore);
        page += 1;
        if (page > 5000) break;
      }

      const data = { success: true, data: merged };
      await cacheService.setCache(this.IMAGES_CACHE_KEY, data, IMAGES_PRESIGN_CACHE_MS);
      console.log('💾 ImageService: Images cached successfully!');

      return data;
    } catch (error) {
      console.error('💥 Error fetching images:', error);
      console.error('Error details:', error.message);
      return { success: false, data: [], error: error.message };
    }
  }

  // Clear images cache (useful for admin operations)
  async clearImagesCache() {
    await cacheService.deleteCache(this.IMAGES_CACHE_KEY);
    console.log('🗑️ ImageService: Images cache cleared');
  }

  // Fetch images by category
  async getImagesByCategory(category) {
    try {
      const response = await fetch(`${this.baseURL}/api/images/category/${category}`);
      if (!response.ok) {
        throw new Error('Failed to fetch images by category');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching images by category:', error);
      return [];
    }
  }

  // Fetch specific image by name (JWT required; basename-only server-side)
  async getImageByName(imageName) {
    try {
      const base =
        typeof imageName === 'string'
          ? imageName.split(/[/\\]/).pop() || ''
          : '';
      if (!base) return null;
      const endpoint = `${this.baseURL}/api/images/name/${encodeURIComponent(base)}`;
      let headers = { ...mergeBlogAuthHeaders() };
      let hasBearerToken = typeof headers.Authorization === 'string' && headers.Authorization.startsWith('Bearer ');

      // In local dev, try minting admin JWT once if missing.
      if (!hasBearerToken && isLocalDev) {
        const blogsApi = config.getFullApiUrl ? config.getFullApiUrl('/api/blogs') : `${this.baseURL}/api/blogs`;
        const minted = await mintAdminJwtFromEnv(blogsApi);
        if (minted) {
          headers = { ...mergeBlogAuthHeaders() };
          hasBearerToken = typeof headers.Authorization === 'string' && headers.Authorization.startsWith('Bearer ');
        }
      }

      // Public mode should not call an admin-protected endpoint.
      if (!hasBearerToken) {
        return null;
      }

      const response = await fetch(endpoint, { headers });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          return null;
        }
        throw new Error(`Failed to fetch image by name (${response.status})`);
      }
      const result = await response.json();
      return result.success ? result.data : null;
    } catch (error) {
      // Keep this quiet in public mode; callers already have local fallbacks.
      console.warn('Error fetching image by name:', error?.message || error);
      return null;
    }
  }

  // Get image URL by name (helper function)
  async getImageUrl(imageName) {
    const image = await this.getImageByName(imageName);
    if (!image) return null;
    return image.url || image.s3_url || null;
  }

  // Get multiple image URLs by names
  async getImageUrls(imageNames) {
    const imageUrls = {};
    for (const name of imageNames) {
      const url = await this.getImageUrl(name);
      if (url) {
        imageUrls[name] = url;
      }
    }
    return imageUrls;
  }
}

export default new ImageService(); 